import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  GetMyPreferencesDocument,
  type RailTileInput,
  UpdatePreferencesDocument,
  type UpdatePreferencesInput,
} from "#app/lib/data";
import { useAppData } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import {
  TILES,
  type RailTileData,
} from "#app/components/navigation/module-rail/tiles";
import {
  HOME_SECTION_IDS,
  type HomeSectionId,
} from "#app/components/home/home-sections";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/**
 * useNavPreferences — the user's navigation preferences (Module Rail compact
 * mode, rail tile order/visibility, Home module-menu section order), persisted on
 * the typed `myPreferences` fields like `workflowOrder`, scoped per
 * (user, workspace).
 *
 * Three layers, mirroring `useWorkflowOrder` plus a first-paint cache:
 *  - Server (`GetMyPreferences`): source of truth once known — cross-device
 *    sync-down. The network fetch is owned by `workspace-data-provider`;
 *    cache-first here avoids a duplicate request.
 *  - localStorage mirror, keyed per workspace: seeds the first paint
 *    synchronously (no flash of default rail width/order on reload) and is
 *    re-warmed whenever the server disagrees.
 *  - Local override state: set on write, cleared when the mutation settles,
 *    so edits are instant without waiting for the cache round-trip.
 */

/** A registry tile resolved against the user's saved order/visibility. */
export type ResolvedRailTile = RailTileData & { show: boolean };

type NavPrefsMirror = {
  compact: boolean;
  /** Saved order/visibility; null until the user customizes (or the server
   * syncs down). Resolution treats null and [] as "registry defaults". */
  railTiles: RailTileInput[] | null;
  homeSectionOrder: string[] | null;
};

const MIRROR_DEFAULTS: NavPrefsMirror = {
  compact: false,
  railTiles: null,
  homeSectionOrder: null,
};

/**
 * Resolve the saved tile list against the static registry. The registry is
 * the source of truth for presentation/routing (`icon`/`label`/`route`/
 * `hasSubmenu`) — saved entries contribute order and `show` only:
 *  - saved ids missing from the registry (removed tiles) are dropped;
 *  - registry tiles missing from saved (new tiles) append at their default
 *    end-of-registry position, shown;
 *  - `home` is pinned first and always shown, regardless of saved payload.
 */
export function resolveRailTiles(
  saved: RailTileInput[] | null,
  registry: RailTileData[],
): ResolvedRailTile[] {
  const byId = new Map(registry.map((tile) => [tile.id, tile]));
  const seen = new Set<string>();
  const ordered: ResolvedRailTile[] = [];
  for (const entry of saved ?? []) {
    const tile = byId.get(entry.id);
    if (!tile || seen.has(entry.id)) continue;
    seen.add(entry.id);
    ordered.push({ ...tile, show: entry.show });
  }
  for (const tile of registry) {
    if (!seen.has(tile.id)) ordered.push({ ...tile, show: true });
  }
  const homeIndex = ordered.findIndex((tile) => tile.id === "home");
  if (homeIndex !== -1) {
    const [home] = ordered.splice(homeIndex, 1);
    if (home) ordered.unshift({ ...home, show: true });
  }
  return ordered;
}

/**
 * Resolve a saved section order against the registry ids: unknown/duplicate
 * ids are dropped, sections missing from saved append in default order.
 */
export function resolveHomeSectionOrder<T extends string>(
  saved: string[] | null,
  registryIds: readonly T[],
): T[] {
  const known = new Set<string>(registryIds);
  const ordered = (saved ?? []).filter(
    (id, index, arr): id is T => known.has(id) && arr.indexOf(id) === index,
  );
  for (const id of registryIds) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  return ordered;
}

/** Strip `__typename` so server tiles round-trip as mutation input. */
function toRailTileInputs(
  tiles: {
    id: string;
    icon: string;
    label: string;
    route: string;
    show: boolean;
  }[],
): RailTileInput[] {
  return tiles.map(({ id, icon, label, route, show }) => ({
    id,
    icon,
    label,
    route,
    show,
  }));
}

export function useNavPreferences() {
  const { currentUser } = useAppData();
  const { activeWorkspace } = useWorkspace();

  // Per-workspace mirror key — preferences are (user, workspace)-scoped, so
  // a shared key would flash workspace A's rail after switching to B.
  const { state: mirror, update: updateMirror } =
    usePersistedState<NavPrefsMirror>(
      `nav-prefs:${activeWorkspace?.workspaceId ?? "none"}`,
      MIRROR_DEFAULTS,
    );

  // Skipped until authed — this hook mounts under providers that also wrap
  // the login screens, where cache-first would still hit the network.
  const { data } = useQuery(GetMyPreferencesDocument, {
    fetchPolicy: "cache-first",
    skip: !currentUser,
  });
  const serverPrefs = data?.myPreferences ?? null;

  const [updatePreferences] = useMutation(UpdatePreferencesDocument);

  // Local overrides — set on write, cleared when the mutation settles.
  const [localCompact, setLocalCompact] = useState<boolean | null>(null);
  const [localRailTiles, setLocalRailTiles] = useState<RailTileInput[] | null>(
    null,
  );
  const [localHomeSectionOrder, setLocalHomeSectionOrder] = useState<
    string[] | null
  >(null);

  // Server wins once known; the mirror covers first paint and pre-auth.
  const compact =
    localCompact ??
    (typeof serverPrefs?.compactRail === "boolean"
      ? serverPrefs.compactRail
      : mirror.compact);
  const savedRailTiles =
    localRailTiles ??
    (serverPrefs ? toRailTileInputs(serverPrefs.railTiles) : mirror.railTiles);
  const savedHomeSectionOrder =
    localHomeSectionOrder ??
    (serverPrefs ? serverPrefs.homeSectionOrder : mirror.homeSectionOrder);

  const railTiles = useMemo(
    () => resolveRailTiles(savedRailTiles, TILES),
    [savedRailTiles],
  );
  const homeSectionOrder = useMemo<HomeSectionId[]>(
    () => resolveHomeSectionOrder(savedHomeSectionOrder, HOME_SECTION_IDS),
    [savedHomeSectionOrder],
  );

  // Re-warm the mirror when the server disagrees (e.g. toggled on another
  // device) so the NEXT load's first paint is already correct. Converges —
  // once equal, no further writes.
  useEffect(() => {
    if (!serverPrefs) return;
    const next: NavPrefsMirror = {
      compact:
        typeof serverPrefs.compactRail === "boolean"
          ? serverPrefs.compactRail
          : mirror.compact,
      railTiles: toRailTileInputs(serverPrefs.railTiles),
      homeSectionOrder: [...serverPrefs.homeSectionOrder],
    };
    if (JSON.stringify(next) !== JSON.stringify(mirror)) {
      updateMirror(next);
    }
  }, [serverPrefs, mirror, updateMirror]);

  const persist = useCallback(
    (input: UpdatePreferencesInput, clearLocal: () => void) => {
      updatePreferences({
        variables: { input },
        onCompleted: clearLocal,
        onError: (err) => {
          clearLocal();
          toastMutationError(err, "Failed to save preference");
        },
      });
    },
    [updatePreferences],
  );

  const setCompact = useCallback(
    (next: boolean) => {
      setLocalCompact(next);
      updateMirror({ compact: next });
      persist({ compactRail: next }, () => setLocalCompact(null));
    },
    [persist, updateMirror],
  );

  const setRailTiles = useCallback(
    (next: ResolvedRailTile[]) => {
      const stored = toRailTileInputs(next);
      setLocalRailTiles(stored);
      updateMirror({ railTiles: stored });
      persist({ railTiles: stored }, () => setLocalRailTiles(null));
    },
    [persist, updateMirror],
  );

  const setHomeSectionOrder = useCallback(
    (next: string[]) => {
      setLocalHomeSectionOrder(next);
      updateMirror({ homeSectionOrder: next });
      persist({ homeSectionOrder: next }, () => setLocalHomeSectionOrder(null));
    },
    [persist, updateMirror],
  );

  return {
    compact,
    setCompact,
    /** Full resolved tile list — ordered, including hidden (`show: false`)
     * tiles so the rail, the More overflow, and the dialog share one view. */
    railTiles,
    setRailTiles,
    homeSectionOrder,
    setHomeSectionOrder,
  };
}
